<template>
  <VanPopup :show="show" @update:show="updateShow" closeable>
    <VanCellGroup inset class="pt-10">
      <VanField
        :model-value="fromTmp === null ? '-∞' : fromTmp"
        is-link
        readonly
        :label="$t('Chats.chatScene.edit.from')"
        required
        @click="clickOpenScoreSliderModal('from')"
      />
      <VanField
        :model-value="toTmp === null ? '+∞' : toTmp"
        is-link
        readonly
        :label="$t('Chats.chatScene.edit.to')"
        required
        @click="clickOpenScoreSliderModal('to')"
      />
      <div class="px-4 bg-base-200 pt-1 pb-2">
        <GenerableTextArea
          v-model="definitionTmp"
          :label="$t('Chats.chatScene.edit.reactionTips')"
          :mateContext="mateContext"
          :promptType="MateCardPromptType.Reaction"
          :chat-scene-name="chatScene.name"
          :chat-scene-description="chatScene.scenario"
          :name="name"
          :from="fromTmp"
          :to="toTmp"
          component-type="targetReaction"
        />
      </div>
      <div class="mx-4 mt-2 mb-5">
        <p class="text-sm text-base-content/50">
          {{ $t("Chats.chatScene.edit.relatedEvent") }}
        </p>

        <div class="flex flex-col gap-2 w-full my-2">
          <div
            v-for="event in props.definition?.eventzz"
            :key="event.id"
            class="text-xs w-full bg-my-dark p-2.5 rounded-md"
          >
            <div class="flex items-center gap-1 justify-between">
              <p class="line-clamp-1">{{ event.name }}</p>
              <button
                class="btn btn-xs btn-neutral cursor-pointer"
                @click="clickRemoveEvent(event.id!)"
              >
                <i class="iconfont icon-delete text-xs" />
              </button>
            </div>
            <p class="text-green-500">
              {{ event.probability }}%
              {{ $t("Chats.chatScene.edit.probability") }}
            </p>
            <p class="text-base-content/50 line-clamp-2">
              {{ event.definition }}
            </p>
          </div>
        </div>

        <div class="flex">
          <button class="btn btn-xs btn-neutral" @click="clickOpenEventSelect">
            <i class="iconify mdi--plus text-xs" />
            {{ $t("Buttons.select") }}
          </button>
        </div>
      </div>
    </VanCellGroup>

    <div class="flex justify-end gap-2 mx-4 mt-2 mb-5">
      <button class="btn btn-sm btn-neutral" @click="clickDelete" v-if="isEdit">
        {{ $t("Buttons.delete") }}
      </button>
      <button class="btn btn-sm btn-primary" @click="clickSave">
        {{ $t("Buttons.save") }}
      </button>
    </div>

    <BaseScoreSliderModal
      v-model:show="showScoreSliderModal"
      :title="showScoreTitle"
      :score="showScore"
      :min="min"
      :max="max"
      @confirm="confirmScore"
      :show-less-infinity="showScoreTitle === $t('Chats.chatScene.edit.from')"
      :show-more-infinity="showScoreTitle === $t('Chats.chatScene.edit.to')"
    />

    <ModalSelectOption
      v-model:show="showEventSelect"
      :options="eventOptions"
      @confirm="confirmEvent"
    />
  </VanPopup>
</template>

<script setup lang="ts">
import { MateCardPromptType } from "@/enums/chatEnums";
import {
  ChatScene,
  ChatSceneEvent,
  ChatSceneScoreDefinition,
} from "@/types/chat";
import { showConfirmDialog, showDialog } from "vant";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  show: boolean;
  definition?: ChatSceneScoreDefinition;
  mateContext?: any;
  name?: string;
  chatScene: ChatScene;
}>();

const emit = defineEmits<{
  (event: "update:show", show: boolean): void;
  (event: "update:definition", definition: ChatSceneScoreDefinition): void;
}>();

const updateShow = (show: boolean) => {
  emit("update:show", show);
};

const showScoreSliderModal = ref(false);
const showScoreTitle = ref("");
const showScore = ref<number | null>(0);
const fromTmp = ref<number | null>(0);
const toTmp = ref<number | null>(0);
const definitionTmp = ref("");
const min = ref(-100);
const max = ref(100);
const { t } = useI18n();
const isEdit = ref(false);
const idxTmp = ref(0);

const eventMap = ref(new Map<number, ChatSceneEvent>());
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      eventMap.value.clear();
      props.chatScene.eventzz?.forEach((event) => {
        eventMap.value.set(event.id!, event);
      });
      if (props.definition) {
        const { from, to, definition, idx } = props.definition;
        fromTmp.value = from;
        toTmp.value = to;
        definitionTmp.value = definition;
        isEdit.value = true;
        idxTmp.value = idx!;
      } else {
        isEdit.value = false;
        fromTmp.value = 0;
        toTmp.value = 0;
        definitionTmp.value = "";
        idxTmp.value = -1;
      }
    }
  }
);

const clickOpenScoreSliderModal = (type: "from" | "to") => {
  if (type === "from") {
    max.value = toTmp.value || 100;
    min.value = -100;
    showScoreTitle.value = t(`Chats.chatScene.edit.from`);
    showScore.value = fromTmp.value;
  } else {
    min.value = fromTmp.value || -100;
    max.value = 100;
    showScoreTitle.value = t(`Chats.chatScene.edit.to`);
    showScore.value = toTmp.value;
  }
  showScoreSliderModal.value = true;
};

const confirmScore = (score: number | null) => {
  if (showScoreTitle.value === t(`Chats.chatScene.edit.from`)) {
    fromTmp.value = score;
  } else {
    toTmp.value = score;
  }
};

const clickSave = () => {
  if (fromTmp.value && toTmp.value && fromTmp.value > toTmp.value) {
    showDialog({
      title: t("Chats.chatScene.edit.name"),
      message: t("Chats.chatScene.edit.nameTip"),
    });
  }
  updateShow(false);
  emit("update:definition", {
    ...props.definition,
    from: fromTmp.value,
    to: toTmp.value,
    definition: definitionTmp.value,
    idx: idxTmp.value,
    eventzz: props.definition?.eventzz,
  });
};

const clickDelete = () => {
  showConfirmDialog({
    message: t("Message.deleteConfirm"),
    confirmButtonText: t("Buttons.delete"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(() => {
      emit("update:definition", {
        ...props.definition,
        from: -9999,
        to: -9999,
        definition: "removed reaction",
        idx: idxTmp.value,
      });
      updateShow(false);
    })
    .catch(() => {
      console.log("cancel");
    });
};

const showEventSelect = ref(false);
const eventOptions = ref([]);
const clickOpenEventSelect = () => {
  if (!props.definition?.eventzz) {
    props.definition!.eventzz = [];
  }
  const hasEvents: Record<number, boolean> = {};
  const eventzz = JSON.parse(JSON.stringify(props.chatScene.eventzz));
  props.definition?.eventzz?.forEach((event) => {
    hasEvents[event.id!] = true;
  });
  eventOptions.value = eventzz
    .filter((event: any) => !hasEvents[event.id])
    .map((event: any) => ({
      text: event.name,
      value: event.id,
    }));

  if (eventOptions.value.length === 0) {
    return showDialog({
      message: t("Message.pleaseAddEventFirst"),
      confirmButtonText: t("Buttons.confirm"),
      showCancelButton: false,
    });
  }

  showEventSelect.value = true;
};

const clickRemoveEvent = (eventId: number) => {
  showConfirmDialog({
    message: t("Message.deleteConfirm"),
    confirmButtonText: t("Buttons.delete"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(() => {
      props.definition?.eventzz?.splice(
        props.definition?.eventzz?.findIndex((event) => event.id === eventId),
        1
      );
    })
    .catch(() => {
      console.log("cancel");
    });
};

const confirmEvent = async (eventId: number) => {
  props.definition?.eventzz?.push(eventMap.value.get(eventId)!);
  showEventSelect.value = false;
};
</script>
